import * as React from 'react'
import { Link, useParams } from 'react-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  ArrowLeft,
  Check,
  CloudOff,
  Crosshair,
  Layers,
  Loader2,
  LocateFixed,
  MapPin,
  Plus,
  Spline,
  TriangleAlert,
} from 'lucide-react'
import { isRecordedPoint, type GeoPoint } from '@lib/dto/geo.dto'
import type { TreeDto } from '@lib/dto/tree.dto'
import type { SyncFailure } from '@/hooks/useTreeSurvey'
import { isPointInRing } from '@lib/helpers/geo'
import {
  DEFAULT_SEASON_START_MONTHS,
  seasonOfDay,
  seasonOfMonth,
} from '@lib/helpers/seasons'
import { formatMinutesShort, sumMinutes } from '@lib/helpers/minutes'
import { parseUuid } from '@lib/validators/uuid.validator'
import { createSiteMap, UNKNOWN_VIEW_ZOOM } from '@/map/basemap'
import { MapLegend } from '@/field-map/MapLegend'
import { TreeSheet } from '@/field-map/TreeSheet'
import type { TreeLastVisitProps } from '@/field-map/TreeLastVisit'
import { useLastVisitTrees } from '@/site-visits/useLastVisit'
import {
  detailForZoom,
  treeMarkerHtml,
  treeMarkerSignature,
  type TreeMarkerProps,
} from '@/field-map/TreeMarker'
import { nextLabel } from '@/field-map/treeOutbox'
import type {
  GeolocationState,
  GeolocationStatus,
} from '@/field-map/useGeolocation'
import { ACCURACY_WARN_M, useGeolocation } from '@/field-map/useGeolocation'
import { useMediaQuery, DESKTOP_QUERY } from '@/hooks/useMediaQuery'
import { MapZoomControls } from '@/map/MapZoomControls'
import { useSite } from '@/hooks/useSite'
import { useTreeSpecies } from '@/hooks/useTreeSpecies'
import { useOrganization } from '@/hooks/useOrganization'
import { useTreeProblemTypes } from '@/hooks/useTreeProblemTypes'
import { useTreeSurvey } from '@/hooks/useTreeSurvey'
import { useSiteVisits } from '@/site-visits/useSiteVisits'
import { VisitBar, canRecordIn } from '@/site-visits/VisitBar'
import { isSameVisit } from '@/site-visits/visitStore'
import { useQuery } from '@tanstack/react-query'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import { addQuestion, removeQuestion } from '@/site-visits/siteRecordChanges'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Meters of drift from the live fix past which a drop counts as hand-placed. */
const MANUAL_PLACEMENT_THRESHOLD_M = 1.5

type Mode = 'idle' | 'placing' | 'moving'

/** A site route whose id is not an id. The survey must not mount for it. */
function MissingSite() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 bg-app px-6 text-center">
      <p className="text-sm font-medium">Site not found</p>
      <p className="text-sm text-muted-text">
        It may have been removed, or the link may be wrong.
      </p>
      <Button variant="outline" asChild>
        <Link to="/clients">Back to clients</Link>
      </Button>
    </div>
  )
}

/**
 * The field survey screen: stand under a tree, drop a pin, say what it is and
 * what it needs.
 *
 * Local state is authoritative for what is drawn and the server is caught up in
 * the background — see `useTreeSurvey`. That is not an optimization: this
 * screen is used in parks, and an edit that depends on a radio is an edit that
 * is lost while someone is standing under the tree it describes.
 */
export function FieldMapPage() {
  const params = useParams<{ siteId: string }>()
  const siteId = parseUuid(params.siteId)
  if (siteId === null) return <MissingSite />
  return <FieldMap siteId={siteId} />
}

function FieldMap({ siteId }: { siteId: string }) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<L.Map | null>(null)
  const markersRef = React.useRef(new Map<string, L.Marker>())
  /** What each pin was last DRAWN as — see `treeMarkerSignature`. */
  const signaturesRef = React.useRef(new Map<string, string>())
  const meMarkerRef = React.useRef<L.CircleMarker | null>(null)
  const accuracyRef = React.useRef<L.Circle | null>(null)
  const didInitialCenter = React.useRef(false)
  const boundaryRef = React.useRef<L.Polygon | null>(null)
  const bottomRef = React.useRef<HTMLDivElement | null>(null)
  const inspectorRef = React.useRef<HTMLElement | null>(null)
  const lastPannedRef = React.useRef<string | null>(null)

  const site = useSite(siteId)
  const species = useTreeSpecies()
  const problemTypes = useTreeProblemTypes()
  const organization = useOrganization()
  const siteVisits = useSiteVisits(siteId)
  /** An earlier visit somebody chose to look at; null for the newest. */
  const [viewedVisitId, setViewedVisitId] = React.useState<string | null>(
    null,
  )
  const newest = siteVisits.current('trees')
  const viewed =
    viewedVisitId === null
      ? null
      : (siteVisits
          .all('trees')
          .find((visit) => isSameVisit(visit, viewedVisitId)) ?? null)
  /*
   * An earlier visit, chosen from the visit bar: its findings, read or (once
   * somebody chooses Edit it) edited, on the site as it stood then. The site
   * record itself is only changed as it stands now (docs/site-visits.md).
   */
  const earlierId =
    viewed !== null && newest !== null && !isSameVisit(newest, viewed.id)
      ? viewed.id
      : null
  const viewingEarlier = earlierId !== null
  // The visit whose findings the pins show: the newest that looked at trees,
  // or the earlier one chosen.
  const working = viewingEarlier ? viewed : newest
  /** An earlier visit somebody chose to carry on with; see `canRecordIn`. */
  const [unlockedVisitId, setUnlockedVisitId] = React.useState<string | null>(
    null,
  )
  const canRecordInVisit = canRecordIn(working, unlockedVisitId)
  /*
   * "Now", for the seasons work is recorded in: the season of the visit being
   * worked in, which is what a proposal counts its seasons from
   * (docs/seasonal-work.md). For a visit started today that is today; with
   * none open, it is today anyway.
   */
  const seasonStartMonths =
    organization.data?.seasonStartMonths ?? DEFAULT_SEASON_START_MONTHS
  const currentSeason =
    working === null
      ? seasonOfMonth(new Date().getMonth() + 1, seasonStartMonths)
      : seasonOfDay(working.startedOn, seasonStartMonths)
  const survey = useTreeSurvey(siteId, {
    requireItemizedEstimates:
      organization.data?.requireItemizedEstimates ?? false,
    visit:
      working === null
        ? null
        : {
            id: working.id,
            joinedIds: working.joinedIds,
            pending: working.pending,
          },
    canRecord: canRecordInVisit,
    canSend: siteVisits.isSent,
  })
  // And only once what is shown is this visit's own: see `findingsReady`.
  const canRecordFindings = canRecordInVisit && survey.findingsReady
  /*
   * On a revisit, adding or deleting a tree asks which it was: the site
   * changing, or last visit's record being wrong (docs/site-visits.md). Its
   * pin, species and label never ask: trees do not move.
   */
  const isRevisit = siteVisits.isRevisit(
    'trees',
    canRecordInVisit ? (working?.id ?? null) : null,
  )
  const addTreeQuestion = isRevisit ? addQuestion('TREE') : null
  /*
   * A change made in no visit (at a desk, or before one is started) asks
   * when it happened beside its answer, since no visit dates it.
   */
  const asksWhen = isRevisit && !canRecordInVisit
  const removeTreeQuestion = isRevisit ? removeQuestion('TREE') : null
  const findingsLoading = canRecordInVisit && !survey.findingsReady
  // Where things stood on the earlier visit shown, from the site's history.
  const asOf = useQuery({
    queryKey: siteVisitKeys.map(siteId, earlierId ?? 'none'),
    queryFn: () => siteVisitService.mapAsOf(earlierId ?? ''),
    enabled: earlierId !== null,
    retry: false,
  })
  const asOfTrees = earlierId === null ? null : (asOf.data?.trees ?? null)
  /*
   * What the visit before this one found, for each tree's sheet to show and
   * bring forward with Still here. Read only while there is a visit to bring
   * anything into.
   */
  const lastVisit = useLastVisitTrees(
    siteId,
    canRecordInVisit ? siteVisits.previous('trees', working) : null,
  )
  const trees = survey.trees

  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [mode, setMode] = React.useState<Mode>('idle')
  const [zoom, setZoom] = React.useState(UNKNOWN_VIEW_ZOOM)
  const [follow, setFollow] = React.useState(true)
  const [legendOpen, setLegendOpen] = React.useState(false)
  /**
   * Gray out trees that already have a time estimate, so the ones still
   * missing — the same ones that make a proposal total a floor — keep their
   * color and are the pins you can see.
   */
  const [dimPriced, setDimPriced] = React.useState(false)
  /**
   * Where the pin being moved currently is, updated during the drag.
   *
   * Only the tree list is committed on drop, which is right — re-rendering
   * ninety markers per animation frame to keep a readout live would be absurd.
   * But exactly one marker is draggable at a time, and knowing whether it is
   * inside the property while you are still holding it is the whole point of
   * the warning, so that single position is tracked separately.
   */
  const [draggingAt, setDraggingAt] = React.useState<GeoPoint | null>(null)

  const geo = useGeolocation(true)
  const fix = geo.fix

  /**
   * Above this width the tree editor becomes a column beside the map instead of
   * a sheet across the bottom of it. On a 900px-tall desktop the sheet's 68%
   * left the map a letterbox strip, with species chips stretched to 700px — a
   * phone form blown up rather than a desktop layout.
   */
  const isDesktop = useMediaQuery(DESKTOP_QUERY)

  const boundaryVertices = site.data?.boundary?.vertices ?? null

  /**
   * The trees that fall outside the drawn outline.
   *
   * Derived on every render rather than stored on the tree. A stored flag would
   * be wrong the moment someone redraws the boundary — the trees would not have
   * moved, but the answer would have changed — and nothing would go back to
   * update them. The computation is a ray cast per tree over a ring of tens of
   * points, which at survey scale costs nothing.
   */
  /**
   * Whether the crosshair is outside the outline, while a pin is being placed.
   *
   * Null when there is no boundary to be inside or outside of, and while not
   * placing — there is nothing to answer about.
   *
   * This existed only for DRAGGING an existing pin, which left the commonest
   * case unanswered: a new tree got no boundary feedback at all until after it
   * had been dropped and the sheet had opened. The warning is worth more before
   * the decision than after it.
   *
   * Tracked on `move` rather than `moveend` so it keeps up with a slow pan. It
   * is a boolean, so React re-renders only when the answer actually changes —
   * which over a pan across a boundary is once.
   */
  const [placingOutside, setPlacingOutside] = React.useState<boolean | null>(null)

  const outsideIds = React.useMemo(() => {
    if (boundaryVertices === null) return new Set<string>()
    return new Set(
      trees
        .filter((tree) => !isPointInRing(tree, boundaryVertices))
        .map((tree) => tree.id),
    )
  }, [trees, boundaryVertices])

  /**
   * The site's estimated crew time, and how much of it is still unknown.
   *
   * Summed as integers, so ninety of them cannot drift. The count of
   * unestimated trees is shown alongside because a total that quietly excludes
   * eleven trees is worse than no total: it looks complete.
   */
  const labor = React.useMemo(() => {
    const total = sumMinutes(trees.map((tree) => tree.estimatedLaborMinutes))
    const missing = trees.filter(
      (tree) => tree.estimatedLaborMinutes === null,
    ).length
    return {
      total: trees.length === 0 ? null : formatMinutesShort(total),
      missing,
    }
  }, [trees])

  const selected = trees.find((tree) => tree.id === selectedId) ?? null

  /*
   * Where each pin is drawn: where the tree stands, or on an earlier visit
   * where it stood then, with the trees added since left off. Drawing only:
   * the sheet edits the tree as it stands, so nothing here is written back.
   */
  const pinTrees = React.useMemo(() => {
    if (asOfTrees === null) return trees
    const then = new Map(asOfTrees.map((tree) => [tree.id, tree]))
    return trees.flatMap((tree) => {
      const was = then.get(tree.id)
      // A coordinate that did not read has no place to draw the pin.
      if (was === undefined || !isRecordedPoint(was)) return []
      return [
        {
          ...tree,
          label: was.label,
          species: was.species,
          latitude: was.latitude,
          longitude: was.longitude,
        },
      ]
    })
  }, [trees, asOfTrees])
  /** Trees that stood on the earlier visit and have come down since. */
  const goneSince = React.useMemo(
    () =>
      asOfTrees === null
        ? []
        : asOfTrees
            .filter(isRecordedPoint)
            .filter((then) => !trees.some((tree) => tree.id === then.id)),
    [trees, asOfTrees],
  )

  /* -- map lifecycle ------------------------------------------------- */

  React.useEffect(() => {
    const container = containerRef.current
    if (container === null) return undefined

    // Captured for the cleanup closure: the Map instance itself is created once
    // and never reassigned, so this is the same object the sync effect uses.
    const markers = markersRef.current

    const map = createSiteMap(container)

    /*
     * The "have we framed the view yet" flag belongs to THIS map, not to the
     * component. React re-runs effects on mount in development, which destroys
     * the first map and builds a second; a flag that survived that would leave
     * the surviving map stuck on the default whole-country view, because
     * something that no longer exists had already been centered.
     */
    didInitialCenter.current = false

    setZoom(map.getZoom())
    map.on('zoomend', () => {
      setZoom(map.getZoom())
    })
    // Panning is an explicit statement that the person wants to look somewhere
    // other than where they are standing, so it ends follow-the-dot.
    map.on('dragstart', () => {
      setFollow(false)
    })

    mapRef.current = map

    /*
     * The panel under the map changes height (the bar, the placement bar,
     * the tree sheet) and the inspector opens beside it at a desk, and each
     * resizes the map without resizing the window. Leaflet only watches the
     * window, so without this its idea of the center goes stale and "Drop
     * tree here" plants the tree away from the crosshair. Measured on a phone
     * after a pan (the placement bar takes 25px from the map): 13px off,
     * about 1.5 m at the zoom a surveyor works at, with trees 8 m apart.
     * Before a pan Leaflet returns a cached center that happens to be right,
     * which is why it went unnoticed.
     */
    const resize = new ResizeObserver(() => {
      map.invalidateSize({ pan: false })
    })
    resize.observe(container)

    return () => {
      resize.disconnect()
      map.remove()
      mapRef.current = null
      boundaryRef.current = null
      markers.clear()
      meMarkerRef.current = null
      accuracyRef.current = null
    }
  }, [])

  /* -- the site outline, for context ---------------------------------- */

  /*
   * Read-only here. Knowing where the property line runs is what tells a
   * surveyor whether the tree they are standing under is actually the client's,
   * which is a question that comes up constantly along a boundary — but editing
   * the outline is a different job with a different screen, and offering the
   * handles here would invite reshaping a park by accident while reaching for a
   * pin.
   */
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return

    const boundary = site.data?.boundary ?? null
    if (boundary === null) {
      boundaryRef.current?.remove()
      boundaryRef.current = null
      return
    }

    const latlngs = boundary.vertices.map((vertex) =>
      L.latLng(vertex.latitude, vertex.longitude),
    )
    if (boundaryRef.current === null) {
      boundaryRef.current = L.polygon(latlngs, {
        className: 'stroke-map-halo',
        weight: 2,
        // Outline only. A shaded property line would compete with the pins,
        // and here the boundary is context rather than the subject.
        fill: false,
        dashArray: '6 4',
        interactive: false,
      }).addTo(map)
    } else {
      boundaryRef.current.setLatLngs(latlngs)
    }
  }, [site.data])

  /* -- first meaningful view ----------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || didInitialCenter.current) return

    if (trees.length > 0) {
      const bounds = L.latLngBounds(
        trees.map((tree) => L.latLng(tree.latitude, tree.longitude)),
      )
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 19 })
      didInitialCenter.current = true
      return
    }

    if (fix !== null) {
      map.setView(L.latLng(fix.latitude, fix.longitude), 20)
      didInitialCenter.current = true
      return
    }

    // No trees and no fix: the drawn outline is the only thing that knows where
    // this site is. Fitting to it beats the whole-country default that used to
    // be all there was.
    const boundary = site.data?.boundary ?? null
    if (boundary !== null) {
      map.fitBounds(
        L.latLngBounds(
          boundary.vertices.map((vertex) =>
            L.latLng(vertex.latitude, vertex.longitude),
          ),
        ),
        { padding: [48, 48], maxZoom: 19 },
      )
      didInitialCenter.current = true
    }
  }, [trees, fix, site.data])

  /* -- the "you are here" dot ---------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return

    if (fix === null) {
      meMarkerRef.current?.remove()
      accuracyRef.current?.remove()
      meMarkerRef.current = null
      accuracyRef.current = null
      return
    }

    const here = L.latLng(fix.latitude, fix.longitude)

    /*
     * The accuracy circle is drawn at the device's own reported radius rather
     * than as fixed decoration. It is the honest version of "you are here":
     * when it swells to cover half the park, the dot in its middle is a guess,
     * and the person can see that before trusting it with a pin.
     */
    if (accuracyRef.current === null) {
      accuracyRef.current = L.circle(here, {
        radius: fix.accuracyM,
        className: 'fill-brand/15 stroke-brand/40',
        weight: 1,
        interactive: false,
      }).addTo(map)
    } else {
      accuracyRef.current.setLatLng(here)
      accuracyRef.current.setRadius(fix.accuracyM)
    }

    if (meMarkerRef.current === null) {
      meMarkerRef.current = L.circleMarker(here, {
        radius: 6,
        weight: 2.5,
        className: 'fill-brand stroke-map-halo',
        interactive: false,
      }).addTo(map)
    } else {
      meMarkerRef.current.setLatLng(here)
    }

    if (follow) {
      map.panTo(here, { animate: true, duration: 0.4 })
    }
  }, [fix, follow])

  /* -- is the crosshair inside the outline? ---------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return undefined
    if (mode !== 'placing' || boundaryVertices === null) {
      setPlacingOutside(null)
      return undefined
    }

    function report(): void {
      const center = map?.getCenter()
      if (center === undefined || boundaryVertices === null) return
      setPlacingOutside(
        !isPointInRing(
          { latitude: center.lat, longitude: center.lng },
          boundaryVertices,
        ),
      )
    }

    report()
    map.on('move', report)
    return () => {
      map.off('move', report)
    }
  }, [mode, boundaryVertices])

  /* -- tree markers --------------------------------------------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return

    const detail = detailForZoom(zoom)
    const markers = markersRef.current
    const signatures = signaturesRef.current

    const live = new Set(pinTrees.map((tree) => tree.id))
    for (const [id, marker] of markers) {
      if (!live.has(id)) {
        marker.remove()
        markers.delete(id)
        signatures.delete(id)
      }
    }

    for (const tree of pinTrees) {
      const isSelected = tree.id === selectedId
      const isMoving = isSelected && mode === 'moving'
      const markerProps: TreeMarkerProps = {
        tree,
        // The selected pin ignores the zoom tiers and always draws in full.
        // Zoom thresholds exist to stop ninety pins colliding; one pin cannot
        // collide with itself, and "tap it to see what it is" should work at
        // whatever zoom the person happens to be at.
        detail: isSelected ? 'full' : detail,
        selected: isSelected,
        moving: isMoving,
        outsideBoundary: outsideIds.has(tree.id),
        dimmed: dimPriced && tree.estimatedLaborMinutes !== null,
      }

      /*
       * Only redraw a pin that actually looks different.
       *
       * Building the icon runs React to a string and replacing it rebuilds the
       * marker's DOM, so doing it for every pin on every pass is what made this
       * screen crawl: editing one tree changes the array's identity, and all
       * ninety were re-rendered for it. Typing a note still changes the tree —
       * it just does not change the PIN, and the signature is what knows the
       * difference.
       */
      const signature = treeMarkerSignature(markerProps)
      const drawnAs = signatures.get(tree.id)

      const makeIcon = (): L.DivIcon =>
        L.divIcon({
          html: treeMarkerHtml(markerProps),
          // Leaflet's default divIcon class paints a white box with a border.
          // The pin supplies its own chrome, so that has to go.
          className: '',
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        })

      let marker = markers.get(tree.id)
      if (marker === undefined) {
        marker = L.marker(L.latLng(tree.latitude, tree.longitude), {
          icon: makeIcon(),
        })
        marker.addTo(map)
        markers.set(tree.id, marker)
      } else {
        if (drawnAs !== signature) {
          marker.setIcon(makeIcon())
        }
        // Position is checked separately: a pin can move without looking any
        // different, which is exactly what dragging one is.
        const at = marker.getLatLng()
        if (at.lat !== tree.latitude || at.lng !== tree.longitude) {
          marker.setLatLng(L.latLng(tree.latitude, tree.longitude))
        }
      }
      signatures.set(tree.id, signature)

      // A selected pin is lifted above its neighbors so the pin being edited
      // is never the one hidden behind another.
      marker.setZIndexOffset(
        isSelected
          ? 1000
          : dimPriced && tree.estimatedLaborMinutes === null
            ? 500
            : 0,
      )

      /*
       * Handlers are rebound every pass rather than attached once. They close
       * over `tree`, and a pin whose handler still references the version of
       * itself from three edits ago writes stale data back on the next drag.
       */
      marker.off('click')
      marker.on('click', () => {
        setSelectedId(tree.id)
        setMode('idle')
      })

      marker.off('drag')
      marker.on('drag', () => {
        const at = marker?.getLatLng()
        if (at === undefined) return
        setDraggingAt({ latitude: at.lat, longitude: at.lng })
      })

      marker.off('dragend')
      marker.on('dragend', () => {
        setDraggingAt(null)
        const moved = marker?.getLatLng()
        if (moved === undefined) return
        survey.upsertTree({
          ...tree,
          latitude: moved.lat,
          longitude: moved.lng,
          // A hand-placed pin has no GPS uncertainty to report: it is exactly
          // where a person said it is.
          accuracyM: null,
          positionSource: 'MANUAL',
        })
      })

      /*
       * Dragging is off unless this pin is explicitly in move mode. Always-on
       * dragging is the obvious implementation and the wrong one: a thumb that
       * meant to pan the map would silently relocate a tree that had already
       * been verified, and nothing about the result would look wrong later.
       * `setIcon` rebuilds the marker's DOM and with it the drag handler, so
       * this has to run after it, every pass.
       */
      if (isMoving) {
        marker.dragging?.enable()
      } else {
        marker.dragging?.disable()
      }
    }
  }, [pinTrees, selectedId, mode, zoom, survey, outsideIds, dimPriced])

  /*
   * On an earlier visit, the trees that have come down since, where they
   * stood: faint, and not opened, since there is nothing left to change.
   */
  React.useEffect(() => {
    const map = mapRef.current
    if (map === null || goneSince.length === 0) return undefined
    const detail = detailForZoom(zoom)
    const layer = L.layerGroup().addTo(map)
    for (const tree of goneSince) {
      L.marker(L.latLng(tree.latitude, tree.longitude), {
        interactive: false,
        icon: L.divIcon({
          html: treeMarkerHtml({
            tree,
            detail,
            selected: false,
            moving: false,
            outsideBoundary: false,
            dimmed: true,
          }),
          className: '',
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      }).addTo(layer)
    }
    return () => {
      layer.remove()
    }
  }, [goneSince, zoom])

  /* -- keep the selected pin out from under the panel ----------------- */

  React.useEffect(() => {
    const map = mapRef.current
    if (map === null) return

    if (selectedId === null) {
      lastPannedRef.current = null
      return
    }

    // Re-pan when the selection changes AND when the panel resizes between the
    // full sheet and the compact move bar, since both change how much map is
    // actually visible.
    const key = `${selectedId}:${mode}`
    if (lastPannedRef.current === key) return

    const tree = trees.find((candidate) => candidate.id === selectedId)
    if (tree === undefined) return

    lastPannedRef.current = key
    panIntoFreeSpace(map, L.latLng(tree.latitude, tree.longitude), {
      // The editor is below the map on a phone and beside it on a desktop, so
      // the pin has to be nudged out from under a different edge each time.
      bottomPx: bottomRef.current?.offsetHeight ?? 0,
      rightPx: inspectorRef.current?.offsetWidth ?? 0,
    })
  }, [selectedId, mode, trees, isDesktop])

  /* -- actions -------------------------------------------------------- */

  /**
   * Drop a tree at the crosshair.
   *
   * The crosshair is the map's center, not the GPS dot, which is what makes
   * fine placement possible: the person pans the map under a fixed reticle
   * instead of trying to drop a pin under their own fingertip. Whether the
   * result counts as a GPS position or a hand-placed one is then decided by how
   * far they moved it, rather than by asking them.
   *
   * The id is generated HERE rather than by the server. That is what lets a
   * tree recorded with no signal be edited, referenced and eventually synced
   * without ever waiting for a round trip.
   */
  function handleDropTree(isCorrection?: boolean, changedOn?: string): void {
    const map = mapRef.current
    if (map === null) return

    const center = map.getCenter()
    const driftM =
      fix === null
        ? Number.POSITIVE_INFINITY
        : map.distance(center, L.latLng(fix.latitude, fix.longitude))
    const placedByHand = driftM > MANUAL_PLACEMENT_THRESHOLD_M
    const now = new Date().toISOString()

    const tree: TreeDto = {
      id: crypto.randomUUID(),
      siteId,
      label: nextLabel(trees),
      // Not a species guess. An unidentified tree is a real state in the field
      // and a work list afterwards; a defaulted "Live oak" is silent bad data.
      // Not identified, which is a real answer rather than a missing one:
      // identification happens outdoors, sometimes from a bare winter canopy.
      speciesId: null,
      species: null,
      latitude: center.lat,
      longitude: center.lng,
      accuracyM: placedByHand || fix === null ? null : fix.accuracyM,
      positionSource: placedByHand ? 'MANUAL' : 'GPS',
      // Not estimated yet, which is different from zero — and is what makes the
      // header's "n to estimate" count a usable work list.
      estimatedLaborMinutes: null,
      dbhInches: null,
      removalOffered: false,
      removalMinutes: null,
      removalNotes: null,
      // Not answered yet, and priced by the formula until somebody says
      // otherwise.
      noPruningRecommended: false,
      pruningPriceOverride: null,
      removalPriceOverride: null,
      priceOverrideNotes: null,
      // Recommended until the arborist says otherwise: optional is the
      // exception they mark, not a default anybody should have to undo.
      isOptional: false,
      problems: [],
      notes: null,
      jobs: [],
      // Set by the sheet when the first pruning work is recorded.
      pruningSeason: null,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    }

    survey.upsertTree(tree, isCorrection, changedOn)
    setSelectedId(tree.id)
    setMode('idle')
  }

  function handleArchiveTree(isCorrection?: boolean, changedOn?: string): void {
    if (selected === null) return
    survey.archiveTree(selected.id, isCorrection, changedOn)
    setSelectedId(null)
    setMode('idle')
  }

  /** What the tree's sheet shows of last visit, on a revisit. */
  function lastVisitOf(treeId: string): TreeLastVisitProps | null {
    if (lastVisit === null) return null
    const startedOn = lastVisit.visit.startedOn
    if (lastVisit.status === 'loading') return { status: 'loading', startedOn }
    if (lastVisit.status === 'missing') return { status: 'missing', startedOn }
    return {
      status: 'ready',
      startedOn,
      found: lastVisit.findings.get(treeId) ?? null,
    }
  }

  function handleToggleMove(): void {
    setMode((current) => (current === 'moving' ? 'idle' : 'moving'))
  }

  function handleRecenter(): void {
    const map = mapRef.current
    if (map === null || fix === null) return
    setFollow(true)
    map.setView(
      L.latLng(fix.latitude, fix.longitude),
      Math.max(map.getZoom(), 20),
    )
  }

  /* -- render --------------------------------------------------------- */

  const accuracyIsPoor = fix !== null && fix.accuracyM > ACCURACY_WARN_M
  const siteName = site.data?.name ?? 'Site survey'
  const backTo = site.data === undefined ? '/clients' : `/clients/${site.data.clientId}`

  /**
   * The bottom panel has four states, so it is a helper rather than a chain of
   * ternaries in the JSX. Move mode gets its own compact bar instead of the
   * full sheet for a concrete reason: the sheet is 68% of the screen, and you
   * cannot drag a pin you cannot see.
   */
  function renderBottomPanel(): React.ReactNode {
    if (selected !== null && mode === 'moving') {
      return (
        <MoveBar
          tree={selected}
          onDone={handleToggleMove}
          outsideBoundary={
            boundaryVertices === null
              ? null
              : !isPointInRing(draggingAt ?? selected, boundaryVertices)
          }
        />
      )
    }

    if (selected !== null && !isDesktop) {
      return (
        <TreeSheet
          // Remount per tree: see the note on TreeSheet.
          key={selected.id}
          tree={selected}
          species={species.data ?? []}
          problemTypes={problemTypes.data ?? []}
          ratePerHour={organization.data?.treeLaborRatePerHour ?? null}
          requireItemizedEstimates={
            organization.data?.requireItemizedEstimates ?? false
          }
          currentSeason={currentSeason}
          outsideBoundary={outsideIds.has(selected.id)}
          isOnline={survey.isOnline}
          isPendingSync={
            survey.pendingTreeIds.has(selected.id) || working?.pending === true
          }
          canRecordFindings={canRecordFindings}
          canEditRecord={!viewingEarlier}
          findingsLoading={findingsLoading}
          visitId={working?.id ?? null}
          lastVisit={lastVisitOf(selected.id)}
          onChange={survey.upsertTree}
          onMove={handleToggleMove}
          onDelete={handleArchiveTree}
          removeQuestion={removeTreeQuestion}
          asksWhen={asksWhen}
          onClose={() => {
            setSelectedId(null)
            setMode('idle')
          }}
        />
      )
    }

    if (mode === 'placing') {
      return (
        <div className="space-y-2 border-t border-default bg-surface px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {placingOutside === null ? (
            <p className="text-center text-xs text-muted-text">
              Line the crosshair up with the tree, then drop the pin.
            </p>
          ) : (
            <p
              className={cn(
                'flex items-center justify-center gap-1 text-center text-xs',
                placingOutside ? 'text-warning' : 'text-muted-text',
              )}
            >
              {placingOutside ? (
                <>
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                  Outside the boundary — drop it anyway if the tree really is
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  Inside the boundary
                </>
              )}
            </p>
          )}
          {addTreeQuestion === null ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-11 flex-1"
                onClick={() => {
                  setMode('idle')
                }}
              >
                Cancel
              </Button>
              <Button
                className="h-11 flex-1"
                onClick={() => {
                  handleDropTree()
                }}
              >
                <MapPin className="h-4 w-4" />
                Drop tree here
              </Button>
            </div>
          ) : (
            // A revisit: dropping the pin says which kind of tree it is.
            <div className="flex gap-2">
              <Button
                variant="ghost"
                className="h-11 shrink-0"
                onClick={() => {
                  setMode('idle')
                }}
              >
                Cancel
              </Button>
              <ChangeAnswers
                question={addTreeQuestion}
                className={cn(
                  'min-w-0 flex-1 [&>button]:flex-1',
                  // Room for the date only by wrapping onto a second line.
                  asksWhen ? null : 'flex-nowrap',
                )}
                buttonClassName="h-11 px-2"
                askWhen={asksWhen}
                onAnswer={handleDropTree}
              />
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2 border-t border-default bg-surface px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <GpsChip state={geo} poor={accuracyIsPoor} />
        <Button
          variant="outline"
          aria-pressed={dimPriced}
          aria-label="Gray out trees that already have a time estimate"
          className={cn(
            'ml-auto h-11 shrink-0 px-3',
            dimPriced && 'border-warning bg-warning-subtle text-warning',
          )}
          onClick={() => {
            setDimPriced((on) => !on)
          }}
        >
          <TriangleAlert className="h-4 w-4" />
          <span className="hidden sm:inline">Unpriced</span>
        </Button>
        {/* A tree added changes the site, as it stands now. */}
        {viewingEarlier ? null : (
          <Button className="h-11 shrink-0 px-4" onClick={handleStartPlacing}>
            <Plus className="h-4 w-4" />
            Add tree
          </Button>
        )}
      </div>
    )
  }

  /** Show an earlier visit, or the newest again. */
  function handleViewVisit(visitId: string | null): void {
    setViewedVisitId(visitId)
    setSelectedId(null)
    setMode('idle')
  }

  function handleStartPlacing(): void {
    setMode('placing')
    setSelectedId(null)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-app">
      <header className="z-20 flex h-12 shrink-0 items-center justify-between gap-2 border-b border-default bg-surface px-2">
        <div className="flex min-w-0 items-center gap-1">
          <Button variant="ghost" size="icon" className="h-9 w-9" asChild>
            <Link to={backTo} aria-label="Back to client">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{siteName}</p>
            {/*
              Every count about the survey lives on this one line, and only
              sync status sits on the right. Spread across both, they overran a
              390px phone and the last one truncated mid-word — which is worse
              than not showing it, because a clipped warning still costs the
              space it failed to use.
            */}
            <p className="flex items-center gap-1.5 truncate text-xs text-muted-text">
              {trees.length === 0
                ? 'No trees yet'
                : `${String(trees.length)} ${trees.length === 1 ? 'tree' : 'trees'}`}
              {labor.total !== null ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span
                    className={cn(
                      'tabular-nums',
                      labor.missing > 0 ? 'text-warning' : null,
                    )}
                    title={
                      labor.missing > 0
                        ? `${String(labor.missing)} of ${String(trees.length)} trees not estimated yet`
                        : undefined
                    }
                  >
                    {/*
                      The trailing "+" is doing real work: it says this total is
                      a floor, not the answer. A bare "4.95h" over a site where
                      three trees have no estimate reads as complete, and a
                      quote built on it would be short.
                    */}
                    {labor.total}
                    {labor.missing > 0 ? '+' : null}
                  </span>
                </>
              ) : null}
              {outsideIds.size > 0 ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-warning tabular-nums">
                    {outsideIds.size} outside
                  </span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <SyncStatus
            lostOperations={survey.lostOperations}
            heldCount={survey.heldTreeIds.length}
            pendingCount={survey.pendingCount}
            isSyncing={survey.isSyncing}
            isOnline={survey.isOnline}
            syncError={survey.syncError}
          />
          <Button
            variant={legendOpen ? 'secondary' : 'ghost'}
            size="icon"
            className="h-9 w-9"
            onClick={() => {
              setLegendOpen((open) => !open)
            }}
            aria-label="Toggle legend"
          >
            <Layers className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <VisitBar
        kind="trees"
        visits={siteVisits}
        working={working}
        unlockedId={unlockedVisitId}
        onUnlock={setUnlockedVisitId}
        isOnline={survey.isOnline}
        onView={handleViewVisit}
        viewingEarlier={viewingEarlier}
      />
      {viewingEarlier && asOf.isError ? (
        <p className="shrink-0 border-b border-default bg-surface px-3 py-1.5 text-xs text-secondary-text">
          Where things stood then needs a signal, so the trees are drawn where
          they stand now.
        </p>
      ) : null}

      {/*
        No outline yet.
        
        Worth saying out loud, because the absence is silent otherwise: without
        a boundary nothing can be flagged as off the property, so every pin
        looks fine — and then some sprout amber marks later, when somebody
        traces the outline, with no explanation of what changed.

        A note rather than a locked door. Surveying without a boundary is
        perfectly valid work; the trees are real either way and the warnings
        apply themselves retroactively. Someone standing under a tree should
        not be sent back to a desk.
      */}
      {site.data !== undefined && site.data.boundary === null ? (
        <div className="z-20 flex shrink-0 items-center gap-2 border-b border-default bg-warning-subtle px-3 py-1.5 text-xs">
          <Spline className="h-3.5 w-3.5 shrink-0 text-warning" />
          <span className="min-w-0 text-secondary-text">
            No site outline yet — trees outside it will not be flagged.
          </span>
          <Link
            to={`/sites/${siteId}/boundary?from=trees`}
            className="ml-auto shrink-0 font-medium text-brand underline-offset-2 hover:underline"
          >
            Draw it
          </Link>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="relative min-h-0 flex-1">
            <div ref={containerRef} className="absolute inset-0 z-0" />

          {mode === 'placing' ? <PlacementCrosshair /> : null}

          {legendOpen ? (
            <MapLegend species={species.data ?? []} className="absolute top-2 right-2 z-10" />
          ) : null}

          {geo.message !== null ? (
            <div className="absolute top-2 left-2 z-10 flex max-w-[calc(100%-1rem)] items-start gap-2 rounded-lg border border-default bg-surface px-2.5 py-2 shadow-menu">
              <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-warning" />
              <p className="text-xs leading-snug">{geo.message}</p>
            </div>
          ) : null}

          {isDesktop ? (
            <MapZoomControls
              className="absolute right-3 bottom-16 z-10"
              onZoomIn={() => {
                mapRef.current?.zoomIn()
              }}
              onZoomOut={() => {
                mapRef.current?.zoomOut()
              }}
            />
          ) : null}

          {fix !== null ? (
            <button
              type="button"
              onClick={handleRecenter}
              className={cn(
                'absolute right-3 bottom-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border bg-surface shadow-menu transition-colors lg:h-9 lg:w-9',
                follow
                  ? 'border-brand text-brand'
                  : 'border-default text-secondary-text',
              )}
              aria-label="Center on my location"
            >
              <LocateFixed className="h-5 w-5" />
            </button>
          ) : null}
        </div>

          <div ref={bottomRef} className="z-20 shrink-0">
            {renderBottomPanel()}
          </div>
        </div>

        {/*
          The inspector. Rendered only at desktop widths and only for a
          selected tree — the design system's rule is that a split view is
          opened by a selection, never sitting empty by default.

          Its width comes from the shared `--ds-inspector` token so the map
          screens and any future split view agree without a second opinion.
        */}
        {isDesktop && selected !== null ? (
          <aside
            ref={inspectorRef}
            className="relative z-20 w-[var(--ds-inspector)] shrink-0"
          >
            {/*
              Out of flow on purpose: the tray is a scroller, so its height has
              to come from the screen and never from what is inside it.

              The survey page cannot be given a fixed height — `ProtectedRoute`
              holds the whole app to `min-h-svh` so ordinary pages can grow —
              which meant a tray left in flow made the row exactly as tall as
              the form. A tree with a few problems took the page to 2.6x the
              window, stretched the map to match, and left the tray with
              nothing to scroll of its own — the only way down to its Done
              button was to scroll the whole page, map and all.

              Absolutely positioned, the aside's height is whatever the map
              column leaves it, and the tray scrolls inside that.
            */}
            <div className="absolute inset-0">
              <TreeSheet
                species={species.data ?? []}
                problemTypes={problemTypes.data ?? []}
                ratePerHour={organization.data?.treeLaborRatePerHour ?? null}
                requireItemizedEstimates={
                  organization.data?.requireItemizedEstimates ?? false
                }
                currentSeason={currentSeason}
                isOnline={survey.isOnline}
                isPendingSync={
                  survey.pendingTreeIds.has(selected.id) ||
                  working?.pending === true
                }
                canRecordFindings={canRecordFindings}
                canEditRecord={!viewingEarlier}
                findingsLoading={findingsLoading}
                visitId={working?.id ?? null}
                lastVisit={lastVisitOf(selected.id)}
                key={selected.id}
                variant="inspector"
                tree={selected}
                outsideBoundary={outsideIds.has(selected.id)}
                onChange={survey.upsertTree}
                onMove={handleToggleMove}
                onDelete={handleArchiveTree}
                removeQuestion={removeTreeQuestion}
                asksWhen={asksWhen}
                onClose={() => {
                  setSelectedId(null)
                  setMode('idle')
                }}
              />
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Pan so a point sits in the middle of whatever map is still visible once the
 * editor has taken its share — below it on a phone, beside it on a desktop.
 *
 * Without this, selecting a pin near an edge hides it behind the panel that
 * just opened to edit it — and in move mode that is fatal, since the thing
 * being dragged is under the controls doing the dragging.
 */
function panIntoFreeSpace(
  map: L.Map,
  latlng: L.LatLng,
  insets: { bottomPx: number; rightPx: number },
): void {
  const size = map.getSize()
  const visibleWidth = size.x - insets.rightPx
  const visibleHeight = size.y - insets.bottomPx
  if (visibleWidth <= 0 || visibleHeight <= 0) return

  const zoom = map.getZoom()
  const target = map.project(latlng, zoom)
  // Where the point should land on screen, versus where the map center lands.
  // The difference is how far the center has to move.
  const center = target.add(
    L.point(size.x / 2 - visibleWidth / 2, size.y / 2 - visibleHeight / 2),
  )
  map.panTo(map.unproject(center, zoom), { animate: true, duration: 0.3 })
}

/**
 * The fixed reticle used to place a pin.
 *
 * Pinned to the center of the map rather than following a finger, because a
 * thumb covers the exact spot it is trying to be precise about. Panning the map
 * beneath a stationary target is how every mapping app solves this, and it is
 * the difference between 5 m and 1 m placement on a phone.
 */
function PlacementCrosshair() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <Crosshair className="h-10 w-10 text-brand drop-shadow-[0_1px_2px_var(--ds-map-marker-halo)]" />
    </div>
  )
}

/**
 * The compact bar shown while a pin is being repositioned.
 *
 * Everything the full sheet offers is deliberately withdrawn here. Move mode is
 * a single-purpose state — put this pin in the right place — and giving back
 * the species picker and the delete button would both crowd out the map and
 * invite an edit that has nothing to do with the task in hand.
 */
function MoveBar({
  tree,
  onDone,
  outsideBoundary,
}: {
  tree: TreeDto
  onDone: () => void
  /** Null when the site has no boundary to be inside or outside of. */
  outsideBoundary: boolean | null
}) {
  return (
    <div className="flex items-center gap-3 border-t border-default bg-surface px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">Moving {tree.label}</p>
        {outsideBoundary === null ? (
          <p className="truncate text-xs text-muted-text">
            Drag the pin onto the tree.
          </p>
        ) : (
          <p
            className={cn(
              'flex items-center gap-1 truncate text-xs',
              outsideBoundary ? 'text-warning' : 'text-muted-text',
            )}
          >
            {outsideBoundary ? (
              <>
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                Outside the boundary
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5 shrink-0" />
                Inside the boundary
              </>
            )}
          </p>
        )}
      </div>
      <Button className="ml-auto h-11 px-5" onClick={onDone}>
        Done
      </Button>
    </div>
  )
}

/**
 * Whether the survey is safe on the server yet.
 *
 * Shown always, and quietly, because the one thing a surveyor needs to know
 * before walking back to the truck is whether the morning's work has left the
 * phone. "Saved" is the resting state; a count means the queue is still owed,
 * which is information, not an error — the work is on the device either way.
 */
function SyncStatus({
  heldCount,
  pendingCount,
  lostOperations,
  isSyncing,
  isOnline,
  syncError,
}: {
  /** Trees waiting on a person rather than on the network. */
  heldCount: number
  pendingCount: number
  lostOperations: number
  isSyncing: boolean
  isOnline: boolean
  syncError: SyncFailure | null
}) {
  /*
   * Checked before everything else, including "Saving".
   *
   * Work this build cannot read back is gone whatever the radio is doing, and
   * the count of what is still queued no longer describes the survey — so any
   * of the ordinary states would be a reassurance that is not true. This is the
   * one thing here that is an error rather than information.
   */
  if (lostOperations > 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-destructive">
        <TriangleAlert className="h-3.5 w-3.5" />
        {lostOperations} not saved
      </span>
    )
  }

  /*
   * A tree the server would refuse is held out of the queue rather than
   * allowed to fail the whole batch, which means the queue can be empty while
   * an edit sits in the working set going nowhere. Without this the indicator
   * would say "Saved" about work that is not.
   *
   * Ranked above the network states because no amount of signal fixes it: the
   * remedy is on the form — describe the "Other" problem, say what the "Other"
   * job covers, finish the half-split.
   */
  if (heldCount > 0) {
    return (
      <span
        className="flex items-center gap-1 text-xs text-warning"
        title="A tree is missing something it needs before it can be saved."
      >
        <TriangleAlert className="h-3.5 w-3.5" />
        {heldCount} to finish
      </span>
    )
  }

  /*
   * A refusal outranks "Saving", because a retry is already in flight against
   * a batch the server has said no to. Showing a spinner for that is showing
   * progress that is not happening.
   */
  if (syncError?.needsAttention === true) {
    return (
      <span
        className="flex items-center gap-1 text-xs text-destructive"
        title={syncError.message}
      >
        <TriangleAlert className="h-3.5 w-3.5" />
        {pendingCount} stuck
      </span>
    )
  }

  if (isSyncing) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-text">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving
      </span>
    )
  }

  if (pendingCount === 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-text">
        <Check className="h-3.5 w-3.5" />
        Saved
      </span>
    )
  }

  /*
   * Muted only while the queue is simply settling: online, nothing has failed,
   * and the flush is a moment away. Offline has always warned — work that has
   * not left the phone is worth noticing before walking back to the truck — and
   * a failed request now warns too, because a queue that is online and still
   * owed has had something go wrong and should not look like the resting state.
   */
  const isSettling = isOnline && syncError === null
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs',
        isSettling ? 'text-muted-text' : 'text-warning',
      )}
      title={syncError?.message}
    >
      <CloudOff className="h-3.5 w-3.5" />
      {pendingCount} to sync
    </span>
  )
}

/**
 * Captions for a watch that has no fix yet.
 *
 * Keyed by that status union rather than by `string`, so a new state fails
 * the build here until it has a caption. `tracking` is absent because a
 * tracking device has a fix, and this map is only read when it does not.
 */
const GPS_STATUS_TEXT: Record<
  Exclude<GeolocationStatus, 'tracking'>,
  string
> = {
  idle: 'Starting GPS…',
  locating: 'Finding you…',
  denied: 'Location blocked',
  insecure: 'Needs HTTPS',
  unsupported: 'No GPS',
  unavailable: 'No fix',
}

/**
 * The current fix quality, stated in meters.
 *
 * Shown permanently rather than on demand: accuracy is the single number that
 * decides whether the next pin can be trusted, and it changes minute to minute
 * as someone walks under and out from a canopy.
 */
function GpsChip({
  state,
  poor,
}: {
  state: GeolocationState
  poor: boolean
}) {
  if (state.fix === null) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-text">
        <Crosshair className="h-3.5 w-3.5" />
        {GPS_STATUS_TEXT[state.status]}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'flex items-center gap-1.5 text-xs',
        poor ? 'text-warning' : 'text-muted-text',
      )}
    >
      {poor ? (
        <TriangleAlert className="h-3.5 w-3.5" />
      ) : (
        <Crosshair className="h-3.5 w-3.5" />
      )}
      GPS ±{Math.round(state.fix.accuracyM)} m
      {poor ? ' · check the pin by eye' : null}
    </span>
  )
}
